// ABOUTME: Compare pivot mode — current period total vs a comparison total (previous period or same period last year) per row.
// ABOUTME: Reads the backend-provided row.comparison[source]; shows current, comparison, Δ% and a direction bar, with clickable column sorting.
import { Box, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";
import { AggToggle, DeltaPill, DirectionBar } from "./RhPivotPeriodMode";
import { RhPivotTableRow } from "./RhPivotTableRow";
import { pivotRowCursor } from "../../reportUtils";

const round1 = (n) => Math.round(n * 10) / 10;

// source: 'previous' | 'lastYear'. compareLabel: header label for the comparison column.
export const RhPivotCompareMode = ({ rows, source, compareLabel, periods, chartOption, currentReport, setCurrentReport, onRowClick }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // 'YYYY-MM-DD' -> 'DD-MM-YY' so the year is visible (the last-year window differs by year).
    const fmt = (s) => (s ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(2, 4)}` : '');
    const fmtRange = (p) => (p ? `${fmt(p.start)} → ${fmt(p.end)}` : '');
    const currentRange = fmtRange(periods?.current);
    const compareRange = fmtRange(periods?.[source]);

    // Aggregation + sort live on the report so a saved/reopened report keeps the chosen view.
    const agg = currentReport?.pivot?.compareAgg ?? 'total';
    const setAgg = (value) => setCurrentReport(prev => ({
        ...prev,
        pivot: { ...prev?.pivot, compareAgg: value },
    }));
    // Buckets in the current window; comparison windows share the same length/frame.
    const bucketCount = rows?.[0]?.values?.length || 1;

    const sortState = currentReport?.pivot?.compareSort ?? { key: 'delta', dir: 'desc' };

    const onSort = (key) => {
        const next = sortState.key === key
            ? { key, dir: sortState.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' };
        setCurrentReport(prev => ({
            ...prev,
            pivot: { ...prev?.pivot, compareSort: next },
        }));
    };

    const movers = useMemo(() => {
        const mapped = (rows || []).map((row, index) => {
            const cmp = row.comparison?.[source];
            const cur = row.total ?? 0;
            const cmpTotal = cmp?.total ?? null;
            return {
                row,
                index,
                // Δ% is ratio-invariant, so dividing both sides by the bucket count leaves delta unchanged.
                current: agg === 'total' ? cur : round1(cur / bucketCount),
                compare: cmpTotal === null ? null : (agg === 'total' ? cmpTotal : round1(cmpTotal / bucketCount)),
                delta: cmp?.delta ?? null,
            };
        });

        const mult = sortState.dir === 'asc' ? 1 : -1;
        mapped.sort((a, b) => {
            const av = a[sortState.key];
            const bv = b[sortState.key];
            // Nulls always sink to the bottom regardless of direction.
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            return (av - bv) * mult;
        });
        return mapped;
    }, [rows, source, sortState.key, sortState.dir, agg, bucketCount]);

    const totals = useMemo(() => {
        let current = 0;
        let compare = 0;
        let hasCompare = false;
        for (const m of movers) {
            current += m.current || 0;
            if (m.compare !== null) { compare += m.compare; hasCompare = true; }
        }
        const delta = hasCompare && compare > 0 ? round1(((current - compare) / compare) * 100) : null;
        return { current, compare: hasCompare ? compare : null, delta };
    }, [movers]);

    const cellSx = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const rowBorder = `1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`;
    const sortArrow = (key) => (sortState.key === key ? (sortState.dir === 'desc' ? ' ▼' : ' ▲') : '');

    const SortHeader = ({ sortKey, label, sub }) => (
        <Box sx={{ ...cellSx, flexDirection: 'column', gap: 0.25, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(sortKey)}>
            <Text bold={sortState.key === sortKey}>{label}{sortArrow(sortKey)}</Text>
            {sub && <Text light sx={{ fontSize: 12 }}>{sub}</Text>}
        </Box>
    );

    return (
        <Box sx={{ width: 1, pb: 6 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: 56, borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`, position: 'sticky', top: 72, background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0], zIndex: 1 }}>
                <Box 
                    sx={{ 
                        width: 320, 
                        pr: 1.5,
                        display:'flex',
                        justifyContent:'space-between',
                        gap:1,
                    }}
                >
                    <RhPivotTableRow chartOption={chartOption} currentReport={currentReport} setCurrentReport={setCurrentReport} />
                    <AggToggle value={agg} onChange={setAgg} />
                </Box>
                <SortHeader sortKey="current" label={`${t('current_period')}`} sub={currentRange} />
                <SortHeader sortKey="compare" label={`${compareLabel}`} sub={compareRange} />
                <SortHeader sortKey="delta" label="Δ%" />
                <Box sx={cellSx}><Text bold>{t('direction')}</Text></Box>
            </Box>

            {/* Totals */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: 48, borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`, background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100], position: 'sticky', top: 128, zIndex: 1 }}>
                <Box sx={{ width: 320, pl: 1 }}><Text bold>{t('total')}</Text></Box>
                <Box sx={cellSx}><Text bold>{totals.current}</Text></Box>
                <Box sx={cellSx}><Text bold>{totals.compare === null ? '–' : totals.compare}</Text></Box>
                <Box sx={cellSx}><DeltaPill delta={totals.delta} /></Box>
                <Box sx={cellSx}><DirectionBar delta={totals.delta} /></Box>
            </Box>

            {/* Rows */}
            {movers.map(({ row, index, current, compare, delta }) => (
                <Box
                    key={index}
                    onClick={() => onRowClick?.(index)}
                    sx={{ display: 'flex', alignItems: 'center', height: 48, borderBottom: rowBorder, cursor: pivotRowCursor(row), "&:hover": { background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100] } }}
                >
                    <Box sx={{ width: 320, pl: 1, overflow: 'hidden' }}>
                        <Text sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{t(row.name) || t(row.description)}</Text>
                    </Box>
                    <Box sx={cellSx}><Text bold>{current}</Text></Box>
                    <Box sx={cellSx}><Text light>{compare === null ? '–' : compare}</Text></Box>
                    <Box sx={cellSx}><DeltaPill delta={delta} /></Box>
                    <Box sx={cellSx}><DirectionBar delta={delta} /></Box>
                </Box>
            ))}
        </Box>
    );
};
